import React from 'react';
import { Link } from 'react-router-dom';

const ProfileCard = ({ profile }) => {
  // Default icon and color based on profile type
  const getProfileTypeConfig = (type) => {
    switch (type) {
      case 'startup':
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          ),
          color: 'blue',
          label: 'Startup'
        };
      case 'investor':
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          color: 'green',
          label: 'Investor'
        };
      case 'service_provider':
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          ),
          color: 'purple',
          label: 'Service Provider'
        };
      case 'pharmaceutical':
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          ),
          color: 'red',
          label: 'Pharmaceutical'
        };
      default:
        return {
          icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          ),
          color: 'gray',
          label: 'Participant'
        };
    }
  };
  
  const { icon, color, label } = getProfileTypeConfig(profile.profileType);
  const profileData = profile.profileData || {};
  
  // Render different summary content based on profile type
  const renderProfileSummary = () => {
    switch (profile.profileType) {
      case 'startup':
        return (
          <>
            {profileData.mainProduct && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-700">Main Product: {profileData.mainProduct.name}</p>
                <p className="text-sm text-gray-500">
                  {profileData.mainProduct.development_stage} · {profileData.mainProduct.product_category}
                </p>
              </div>
            )}
            <p className="mt-2 text-sm text-gray-500">
              {/* Check for products in profileData or use indicator of pending count */}
              {profileData.products?.length ? 
                `${profileData.products.length} products` : 
                "Products pending"}
            </p>
          </>
        );
      
      case 'investor':
        return (
          <>
            <p className="mt-2 text-sm font-medium text-gray-700">
              {profileData.company_or_fund}
            </p>
            {profileData.investor_types?.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {profileData.investor_types.slice(0, 2).map((type, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    {type}
                  </span>
                ))}
                {profileData.investor_types.length > 2 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    +{profileData.investor_types.length - 2} more
                  </span>
                )}
              </div>
            )}
          </>
        );
      
      case 'service_provider':
        return (
          <>
            <p className="mt-2 text-sm font-medium text-gray-700">
              {profileData.company}
            </p>
            {profileData.service_types?.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {profileData.service_types.slice(0, 2).map((type, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    {type}
                  </span>
                ))}
                {profileData.service_types.length > 2 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    +{profileData.service_types.length - 2} more
                  </span>
                )}
              </div>
            )}
          </>
        );
      
      case 'pharmaceutical':
        return (
          <>
            <p className="mt-2 text-sm font-medium text-gray-700">
              {profileData.company}
            </p>
            {profileData.interest_types?.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {profileData.interest_types.slice(0, 2).map((type, index) => (
                  <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    {type}
                  </span>
                ))}
                {profileData.interest_types.length > 2 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    +{profileData.interest_types.length - 2} more
                  </span>
                )}
              </div>
            )}
          </>
        );
      
      default:
        return (
          <p className="mt-2 text-sm text-gray-500">No additional details available</p>
        );
    }
  };
  
  return (
    <Link 
      to={`/profiles/${profile.profileType}/${profile.user_id}`}
      className="block bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {profile.photo_url ? (
              <img 
                src={profile.photo_url} 
                alt={profile.name || 'Profile'} 
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className={`w-10 h-10 rounded-full bg-${color}-100 flex items-center justify-center text-${color}-600`}>
                {icon}
              </div>
            )}
            <div>
              <h3 className="text-lg font-medium text-gray-900 truncate">
                {profile.name && !profile.name.includes('@') ? profile.name : 'Anonymous'}
              </h3>
              <div className="flex items-center">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`}>
                  {label}
                </span>
                {profile.job_title && (
                  <span className="ml-2 text-sm text-gray-500">
                    {profile.job_title}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {renderProfileSummary()}
        
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            Member since {new Date(profile.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProfileCard; 